import { ChangeDetectionStrategy, Component } from '@angular/core';

import {
  scaleTime,
  scaleLinear,
  extent,
  max,
  axisBottom,
  timeMonth,
  axisLeft,
  line,
  ScaleTime,
  ScaleLinear,
  Axis,
  NumberValue,
  easeCubicOut,
} from 'd3';
import { Selection } from 'd3-selection';

import { DataRow, Breakpoint } from '@fcom/common/interfaces';
import { toISOStringPreserveTimeZone } from '@fcom/common-booking/utils/date-utils';

import { DOT_RADIUS } from './constants';
import {
  TOOLTIP_AVERAGE_HEIGHT,
  TOOLTIP_AVERAGE_WIDTH,
  LINE_CHART_ANIMATION_DURATION,
  DATA_CLASS_NAME,
  HUNDRED_AND_TEN_PERCENT,
  X_AXIS_PADDING_DAYS,
  Y_AXIS_TICKS,
} from '../common/chart/constants';
import { AbstractChartComponent } from '../common/chart/abstract-chart.component';
import { getTickPadding } from '../common/chart/get-tick-padding';
import { EMPTY_TOOLTIP } from '../tooltip/tooltip';

@Component({
  selector: 'fin-line-chart',
  templateUrl: './line-chart.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LineChartComponent extends AbstractChartComponent {
  public draw(
    svgGroup: Selection<SVGGElement, unknown, null, undefined>,
    rows: DataRow[],
    innerWidth: number,
    innerHeight: number,
    breakpoint: Breakpoint
  ): void {
    const x: ScaleTime<number, number> = this.setupXAxis(innerWidth, rows);
    const y: ScaleLinear<number, number> = this.setupYAxis(innerHeight, rows);

    this.drawXAxis(svgGroup, x, innerHeight);
    this.drawYAxis(svgGroup, y, getTickPadding(breakpoint));

    this.horizontalLines(svgGroup, y, innerWidth);

    const path: Selection<SVGPathElement, unknown, null, undefined> = this.linePath(svgGroup, rows, x, y);

    this.animateLine(path);

    this.dots(svgGroup, rows, x, y);
  }

  public update(
    svgGroup: Selection<SVGGElement, unknown, null, undefined>,
    rows: DataRow[],
    innerWidth: number,
    innerHeight: number,
    breakpoint: Breakpoint
  ): void {
    const x: ScaleTime<number, number> = this.setupXAxis(innerWidth, rows);
    const y: ScaleLinear<number, number> = this.setupYAxis(innerHeight, rows);

    svgGroup.selectAll('.lineXAxis').remove();
    this.drawXAxis(svgGroup, x, innerHeight);

    svgGroup.selectAll('.lineYAxis').remove();
    this.drawYAxis(svgGroup, y, getTickPadding(breakpoint));

    svgGroup.selectAll('.horizontal-line').remove();
    this.horizontalLines(svgGroup, y, innerWidth);

    svgGroup.selectAll(`.${DATA_CLASS_NAME}`).remove();
    const path = this.linePath(svgGroup, rows, x, y);
    this.animateLine(path);

    this.dots(svgGroup, rows, x, y);
  }

  private setupXAxis(innerWidth: number, rows: DataRow[]): ScaleTime<number, number> {
    const x = scaleTime().range([0, innerWidth]);
    const xExtent = extent(rows, ({ xValue }: DataRow) => new Date(xValue));
    xExtent[0].setDate(xExtent[0].getDay() - X_AXIS_PADDING_DAYS);
    xExtent[1].setDate(xExtent[1].getDay() + X_AXIS_PADDING_DAYS);
    x.domain(xExtent);
    return x;
  }

  private setupYAxis(innerHeight: number, rows: DataRow[]) {
    const y = scaleLinear().range([innerHeight, 0]);
    y.domain([0, max(rows, (row: DataRow) => row.value) * HUNDRED_AND_TEN_PERCENT]);
    return y;
  }

  private animateLine(path: Selection<SVGPathElement, unknown, null, undefined>): void {
    const length = path.node().getTotalLength && path.node().getTotalLength();

    path
      .attr('stroke-dasharray', `${length} ${length}`)
      .attr('stroke-dashoffset', length)
      .transition()
      .ease(easeCubicOut)
      .attr('stroke-dashoffset', 0)
      .duration(LINE_CHART_ANIMATION_DURATION);
  }

  private linePath(
    svgGroup: Selection<SVGGElement, unknown, null, undefined>,
    rows: DataRow[],
    x: ScaleTime<number, number>,
    y
  ): Selection<SVGPathElement, unknown, null, undefined> {
    return svgGroup
      .append('path')
      .attr('shape-rendering', 'geometricPrecision')
      .attr('class', DATA_CLASS_NAME)
      .data([rows])
      .attr('fill', 'none')
      .attr('stroke', '#0c0243')
      .attr('stroke-width', 1.5)
      .attr('d', this.lineGenerator(x, y));
  }

  private lineGenerator(x: ScaleTime<number, number>, y: ScaleLinear<number, number>) {
    return line<DataRow>()
      .x(({ xValue }: DataRow) => x(new Date(xValue)))
      .y(({ value }: DataRow) => y(value));
  }

  private horizontalLines(
    svgGroup: Selection<SVGGElement, unknown, null, undefined>,
    y: ScaleLinear<number, number>,
    innerWidth: number
  ): void {
    svgGroup
      .selectAll('yGrid')
      .data(y.ticks(Y_AXIS_TICKS).slice(1)) // Remove horizontal line at 0
      .join('line')
      .attr('class', 'horizontal-line')
      .attr('shape-rendering', 'geometricPrecision')
      .attr('x1', 0)
      .attr('x2', innerWidth)
      .attr('y1', (d) => y(d))
      .attr('y2', (d) => y(d))
      .attr('stroke', '#e0e0e0')
      .attr('stroke-width', 0.5);
  }

  private dots(
    svgGroup: Selection<SVGGElement, unknown, null, undefined>,
    rows: DataRow[],
    x: ScaleTime<number, number>,
    y: ScaleLinear<number, number>
  ): void {
    svgGroup
      .selectAll('.dot')
      .data(rows)
      .join('circle')
      .attr('shape-rendering', 'geometricPrecision')
      .attr('stroke-width', 1)
      .attr('stroke-opacity', '1')
      .attr('fill', '#0c0243')
      .attr('class', 'dot')
      .attr('cx', ({ xValue }: DataRow) => x(new Date(xValue)))
      .attr('cy', ({ value }: DataRow) => y(value))
      .on('mouseover', (e: MouseEvent, row: DataRow) => {
        this.tooltip$.next({
          top: this.getTooltipTop(e, TOOLTIP_AVERAGE_HEIGHT),
          left: this.getTooltipLeft(e, TOOLTIP_AVERAGE_WIDTH),
          xValue: toISOStringPreserveTimeZone(row.xValue),
          yValue: row.value,
          visible: true,
          unit: this.unit,
          xAxisType: 'date',
        });
        this.ref.detectChanges();
      })
      .on('mouseout', () => {
        this.tooltip$.next({ ...EMPTY_TOOLTIP, xAxisType: 'date', unit: this.unit });
        this.ref.detectChanges();
      })
      .attr('stroke', '#FFFFFF')
      .attr('r', DOT_RADIUS)
      .raise();
  }

  private drawXAxis(
    svgGroup: Selection<SVGGElement, unknown, null, undefined>,
    x: ScaleTime<number, number>,
    innerHeight: number
  ): void {
    const xAxisText = svgGroup
      .append('g')
      .attr('class', 'lineXAxis')
      .attr('transform', `translate(0, ${innerHeight})`)
      .call(this.xAxis(x))
      .selectAll('text')
      .attr('dy', '1em')
      .attr('stroke', '#676767')
      .attr('fill', '#676767')
      .attr('stroke-width', 0.2)
      .attr('font-family', 'FinnairSans')
      .attr('font-size', '14px')
      .attr('font-weight', 'lighter');

    if (this.tiltXAxisText && this.isMobile) {
      xAxisText.style('text-anchor', 'end').attr('dy', '1.15em').attr('transform', 'rotate(-30)');
    }
  }

  private xAxis(x: ScaleTime<number, number>): Axis<Date | NumberValue> {
    return axisBottom(x)
      .ticks(timeMonth.every(1))
      .tickFormat((domainValue: any) => this.translateXValue(domainValue))
      .tickSize(0);
  }

  private drawYAxis(
    svgGroup: Selection<SVGGElement, unknown, null, undefined>,
    y: ScaleLinear<number, number>,
    tickPadding: number
  ): void {
    svgGroup
      .append('g')
      .attr('class', 'lineYAxis')
      .call(this.yAxis(y, tickPadding))
      .attr('stroke', '#676767')
      .attr('fill', '#676767')
      .attr('stroke-width', 0.2)
      .attr('font-family', 'FinnairSans')
      .attr('font-size', '14px')
      .attr('font-weight', 'lighter')
      .call((g) => g.select('.domain').remove());
  }

  private yAxis(y: ScaleLinear<number, number>, tickPadding: number) {
    return axisLeft(y)
      .ticks(Y_AXIS_TICKS)
      .tickPadding(tickPadding)
      .tickFormat((d: number) => `${d >= 1000 ? d / 1000 : d}${d >= 1000 ? 'k' : ''}`)
      .tickSize(0);
  }
}
